import { z } from 'zod';
import { WAVELENGTH_ORDER, numberParameter, positiveIndex } from './shared.js';

export default {
  label: 'Fringe Zernike Coefficients', group: 'performance',
  description: 'Fringe Zernike coefficients of the wavefront, with the RMS fit error.',
  settingsSchema: z.strictObject({
    wavelength: positiveIndex.describe(`Native wavelength index (1-based), not its wavelength value. ${WAVELENGTH_ORDER}`),
    // Editable default; CODE V documents none (APIReferenceGuide p100, Macro-PLUS p105).
    field: positiveIndex.default(1).describe(`Native field index (1-based).`),
    zoom: positiveIndex.default(1).describe('Native zoom position (1-based). The default 1 is the first position, which every lens has; a lens without ZOO has only that one (PromptingGuide p74; ZoomSystems.17.02, ZOO range 1 to 99).'),
    term_count: z.number().int().min(0).max(37).default(0).describe('ZFRCOEF num_terms: number of Fringe terms in the fit; 0 (the default) delegates the native full set of 37 (APIReferenceGuide p100, Macro-PLUS p105).'),
    rays_across_diameter: z.number().int().nonnegative().default(0).describe('ZFRCOEF nrd: rays across the pupil diameter; 0 (the default) requests the native default. APIReferenceGuide p100 documents 21, but the native 0 argument is passed unchanged.'),
    pupil_coordinates: z.enum(['ENP', 'EXP', 'EXS']).default('ENP').describe('Pupil coordinates: entrance pupil plane (the native default), exit pupil plane or exit pupil sphere (Macro-PLUS p106).'),
  }),
  parameters: [
    numberParameter('wavelength', 'Wavelength (CODE V index)', { integer: true, choices: 'wavelength', prominent: true }),
    numberParameter('field', 'Field', { integer: true }),
    numberParameter('zoom', 'Zoom position', { integer: true, hidden: true }),
    numberParameter('term_count', 'Terms (0 = 37)', { integer: true }),
    numberParameter('rays_across_diameter', 'Rays across diameter (0 = default)', { integer: true }),
    { key: 'pupil_coordinates', label: 'Pupil coordinates', kind: 'select', options: [
      { value: 'ENP', label: 'Entrance pupil plane' },
      { value: 'EXP', label: 'Exit pupil plane' },
      { value: 'EXS', label: 'Exit pupil sphere' },
    ] },
  ],
  coverage: 'One wavelength and field per run. CODE V does not label the units of the coefficients or the RMS fit error.',
} as const;
